/* eslint-disable react-hooks/immutability */
import { Box, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Switch, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickerValue } from "@mui/x-date-pickers/internals";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { CurrencyTextField } from "../../../../components";
import ChildProfileImageUploader from "../../../../components/ChildProfileImageUploader";
import { useForm } from "../../../../components/FormProvider";
import { Child } from "../../../../models";
import { calculateDateDifference, describeDateDifference, removeNonNumericChars } from "../../../../utils";

const NOT_TRAINED = "Not Trained";
const TRAINING = "Training";
const TRAINED = "Trained";

export function EditChildForm(props: {
  child: Child;
  resetFormRef: React.RefObject<(() => void) | null>;
  dataGetterRef: React.RefObject<(() => Child | null) | null>;
}) {
  const { child } = props;
  const form = useForm();

  const [active, setActive] = useState<boolean>(child.isActive ?? false);
  const [birthDate, setBirthDate] = useState<Dayjs | null>(dayjs());
  const [enrollmentStartDate, setEnrollmentStartDate] = useState<Dayjs | null>(dayjs());
  const [enrollmentEndDate, setEnrollmentEndDate] = useState<Dayjs | null>(dayjs());
  const [stage, setStage] = useState<string>(child.stage ?? "");
  const [isTraining, setIsTraining] = useState<boolean>(child.isTraining ?? false);
  const [isTrained, setIsTrained] = useState<boolean>(child.isTrained ?? false);
  const age = birthDate ? describeDateDifference(calculateDateDifference(birthDate.toDate(), new Date())) : null;

  // use refs for the text fields to improve performance. otherwise every character entered/deleted causes a re-render, making it feel very slow
  // it's not as noticable with the other types of inputs, so we don't need to make those ref based.
  const firstNameField = useRef<HTMLInputElement | null>(null);
  const lastNameField = useRef<HTMLInputElement | null>(null);
  const weeklyRateField = useRef<HTMLInputElement | null>(null);

  const resetForm = (): void => {
    setActive(child.isActive ?? false);
    setBirthDate(dayjs(child.birthDate));
    setEnrollmentStartDate(dayjs(child.enrollmentStartCentralDateTime));
    setEnrollmentEndDate(dayjs(child.enrollmentEndCentralDateTime));
    setStage(child.stage ?? "");
    setIsTraining(child.isTraining ?? false);
    setIsTrained(child.isTrained ?? false);
    if (firstNameField.current) {
      firstNameField.current.value = child?.firstName ?? "";
    }
    if (lastNameField.current) {
      lastNameField.current.value = child.lastName ?? "";
    }
    if (weeklyRateField.current) {
      weeklyRateField.current.value = child.weeklyRate?.toString();
    }
  };

  const getUpdatedRecord = (): Child => {
    const formattedEnrollmentStartCentralDateTime =
      enrollmentStartDate && dayjs(enrollmentStartDate).isValid() ? dayjs(enrollmentStartDate).format("YYYY-MM-DD") : undefined;
    const formattedEnrollmentEndCentralDateTime =
      enrollmentEndDate && dayjs(enrollmentEndDate).isValid() ? dayjs(enrollmentEndDate).format("YYYY-MM-DD") : undefined;
    const formattedBirthDate = birthDate ? dayjs(birthDate).format("YYYY-MM-DD") : undefined;

    return {
      ...child,
      isActive: active,
      firstName: firstNameField?.current?.value ?? "",
      lastName: lastNameField?.current?.value ?? "",
      weeklyRate: removeNonNumericChars(weeklyRateField?.current?.value) ?? 0,
      stage,
      isTraining,
      isTrained,
      enrollmentStartCentralDateTime: formattedEnrollmentStartCentralDateTime,
      enrollmentEndCentralDateTime: formattedEnrollmentEndCentralDateTime,
      birthDate: formattedBirthDate,
    };
  };

  useEffect(() => {
    if (props.dataGetterRef) {
      props.dataGetterRef.current = getUpdatedRecord;
    }
    if (props.resetFormRef) {
      props.resetFormRef.current = resetForm;
    }
  });

  const getTrainingValue = () => {
    if (isTraining) {
      return TRAINING;
    } else if (isTrained) {
      return TRAINED;
    } else {
      return NOT_TRAINED;
    }
  };

  useEffect(() => {
    setActive(child.isActive ?? false);
    setBirthDate(dayjs(child.birthDate));
    setEnrollmentStartDate(dayjs(child.enrollmentStartCentralDateTime));
    setEnrollmentEndDate(dayjs(child.enrollmentEndCentralDateTime));
    setStage(child.stage ?? "");
    setIsTrained(child.isTrained ?? false);
    setIsTraining(child.isTraining ?? false);
  }, [child]);

  const handleBirthDateChange = (value: PickerValue) => {
    setBirthDate(dayjs(value));
  };

  const handleEnrollmentStartDateChange = (value: PickerValue) => {
    setEnrollmentStartDate(dayjs(value));
  };

  const handleEnrollmentEndDateChange = (value: PickerValue) => {
    setEnrollmentEndDate(dayjs(value));
  };

  const handleStageChange = (event: SelectChangeEvent) => {
    setStage(event.target.value);
  };

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActive(event.target.checked);
  };

  const handleTrainingChange = (event: SelectChangeEvent) => {
    switch (event.target.value) {
      case NOT_TRAINED: {
        setIsTrained(false);
        setIsTraining(false);
        break;
      }
      case TRAINING: {
        setIsTrained(false);
        setIsTraining(true);
        break;
      }
      case TRAINED: {
        setIsTrained(true);
        setIsTraining(false);
        break;
      }
    }
  };

  return (
    <Stack direction="row" sx={{ flexWrap: "wrap", p: 2 }}>
      <Box sx={{ width: { md: "220px", xs: "100%" }, mb: { xs: 4 } }}>
        <ChildProfileImageUploader childId={child?.id ?? 0} sx={{ justifyItems: { xs: "center" } }} />
      </Box>
      <Stack sx={{ alignItems: { xs: "center", md: "flex-start" } }} flexGrow={1}>
        <Stack gap={1}>
          <FormControlLabel
            control={<Switch checked={active} onChange={handleActiveChange} disabled={form.isDisabled} />}
            label={active ? "Currently Enrolled" : "Inactive"}
          />
          <Stack direction="row" gap={1}>
            <TextField variant="filled" label="First Name" defaultValue={child?.firstName ?? ""} inputRef={firstNameField} disabled={form.isDisabled} />
            <TextField variant="filled" label="Last Name" defaultValue={child?.lastName ?? ""} inputRef={lastNameField} disabled={form.isDisabled} />
          </Stack>
          <CurrencyTextField
            variant="filled"
            label="Weekly Rate"
            sx={{ width: 120 }}
            value={child.weeklyRate?.toString()}
            inputRef={weeklyRateField}
            decimalPoints={0}
            disabled={form.isDisabled}
          />
          <FormControl variant="filled" sx={{ width: 180 }} disabled={form.isDisabled}>
            <InputLabel id="stage-label">Stage</InputLabel>
            <Select
              labelId="stage-label"
              id="stage"
              value={stage}
              onChange={handleStageChange}
              MenuProps={{
                slotProps: {
                  root: { sx: { zIndex: 1600 } },
                  paper: { sx: { zIndex: 1600 } },
                },
              }}
            >
              <MenuItem value="Infant">Infant</MenuItem>
              <MenuItem value="Toddler">Toddler and Up</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="filled" sx={{ width: 180 }} disabled={form.isDisabled}>
            <InputLabel id="training-label">Training</InputLabel>
            <Select
              labelId="training-label"
              id="training"
              value={getTrainingValue()}
              onChange={handleTrainingChange}
              MenuProps={{
                slotProps: {
                  root: { sx: { zIndex: 1600 } },
                  paper: { sx: { zIndex: 1600 } },
                },
              }}
            >
              <MenuItem value={NOT_TRAINED}>Not Training</MenuItem>
              <MenuItem value={TRAINING}>Training</MenuItem>
              <MenuItem value={TRAINED}>Trained</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" gap={1} alignItems="center">
            <DatePicker
              onChange={handleEnrollmentStartDateChange}
              value={enrollmentStartDate}
              label="Enrollment Start"
              sx={{ width: 164 }}
              slotProps={{
                textField: {
                  variant: "filled",
                },
              }}
              disabled={form.isDisabled}
            />
            &mdash;
            <DatePicker
              onChange={handleEnrollmentEndDateChange}
              value={enrollmentEndDate}
              label="Enrollment End"
              sx={{ width: 164 }}
              slotProps={{
                textField: {
                  variant: "filled",
                },
              }}
              disabled={form.isDisabled}
            />
          </Stack>
          <DatePicker
            onChange={handleBirthDateChange}
            value={birthDate}
            label="Birth Date"
            sx={{ width: 164 }}
            slotProps={{
              textField: {
                variant: "filled",
              },
            }}
            disabled={form.isDisabled}
          />
          <Typography variant="body1" sx={{ mt: 1.5, mb: 4 }}>
            Age: {age}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
